import { Plus, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// These mirror the FAQPage JSON-LD in public/index.html so the answers are
// visible on-page (Google needs the Q&A in the rendered HTML, not only in schema).
// Native <details> keeps every answer in the DOM even when collapsed → crawlable.
const FAQS = [
  {
    q: "How long should you stay in Copenhagen?",
    a: "Three to four whole days is ideal. Three days covers the highlights — Nyhavn, Tivoli, the old town and a bakery or two — while a 4th day adds time for more. A 5th day gives you room for a day trip such as Kronborg Castle or the Louisiana museum.",
  },
  {
    q: "Is 2 days enough to see Copenhagen?",
    a: "No… we will recommend at least three days. You will regret only planning for 2 days.",
  },
  {
    q: "Can I see Copenhagen on a layover?",
    a: (
      <>
        Yes — the airport is only 15 minutes from the city centre by Metro. With 6 hours or more
        you can see Nyhavn, the royal quarter and the Round Tower, and still be back in good time.
        Our{" "}
        <Link
          to="/layover"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          layover guide
        </Link>{" "}
        has a realistic hour-by-hour plan, transport included.
      </>
    ),
  },
  {
    q: "How do I get from Copenhagen Airport to the city centre?",
    a: "The Metro (M2) runs straight from the airport to the city centre in about 15 minutes, 24/7 — no changes needed. Heading to Central Station or Vesterbro? Take the train instead. Buy a 3-zone single ticket in the Rejsebillet app or at the red DSB machines.",
    link: { to: "/transport", label: "The full transport guide" },
  },
  {
    q: "Is Copenhagen expensive?",
    a: (
      <>
        It can be, but you can keep costs down: Stay in a hostel in the city centre. Get around by
        walking, biking or Metro. Buy food and drinks from a supermarket. See our{" "}
        <Link
          to="/budget"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          guide to Copenhagen on a budget
        </Link>
        .
      </>
    ),
  },
  {
    q: "How expensive is Copenhagen, really?",
    a: (
      <>
        It has a pricey reputation, but it's manageable with a few local habits. Expect to pay around 40–60
        kr. for a coffee, 60–85 kr. for a beer, 90–160 kr. for a piece of smørrebrød and 600–900 kr.
        for a mid-range dinner for two. The big savers: supermarkets over restaurants and the many
        free sights. Our{" "}
        <Link
          to="/budget"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          budget guide
        </Link>{" "}
        has the full breakdown.
      </>
    ),
  },
  {
    q: "Do I need cash in Copenhagen?",
    a: "Cash is rarely used in Copenhagen — almost all shops, restaurants and attractions accept cards or digital payments.",
  },
  {
    q: "What currency does Copenhagen use?",
    a: "The Danish krone (DKK) — Denmark doesn't use the euro. It's pegged at roughly 7.45 kr. to €1, and cards are accepted absolutely everywhere, so you may never touch cash at all.",
  },
  {
    q: "Do they speak English in Copenhagen?",
    a: "Yes — virtually everyone speaks excellent English, from café staff to bus drivers, and menus and signs usually have English too. You never need Danish, but a friendly 'tak' (thanks) goes a long way.",
  },
  {
    q: "What is the cheapest way to travel in Copenhagen?",
    a: "Copenhagen is walkable, and most sights are within walking distance. Use the planner to group the sights you want with a realistic timetable.",
    link: { to: "/planner", label: "Open the planner" },
  },
  {
    q: "Where should you stay in Copenhagen?",
    a: (
      <>
        The inner city (Indre By) is the great all-rounder — everything walkable, and the Metro
        close by. The Nyhavn area connects to the airport Metro and is also a great choice.
        <span className="block mt-3">
          Vesterbro sits by the Central Station, where the train runs straight to the airport.
        </span>
        <span className="block mt-3">
          Ørestad has lots of hotels — you&apos;ll ride the M1 Metro to Kongens Nytorv (by Nyhavn) or
          the train to the Central Station for the centre, and the train from Ørestad station
          straight back to the airport. Find hand-picked hotels in each area on our{" "}
          <Link
            to="/stay"
            className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
          >
            accommodation page
          </Link>
          .
        </span>
      </>
    ),
  },
  {
    q: "Is Ørestad a good choice to stay?",
    a: "There are lots of hotels out there, but we will recommend a closer option for easy walking to everything. It's outside the city centre so you have to use transport: M1 Metro brings you to Kongens Nytorv (by Nyhavn) or train to Central Station in about 12 minutes.",
    link: { to: "/stay", label: "See the hotels" },
  },
  {
    q: "Is Tivoli expensive?",
    a: (
      <>
        A little, yes — entry alone is on the pricey side, and the unlimited-rides pass costs extra on
        top. But it&apos;s usually a little cheaper on weekdays than weekends —{" "}
        <a
          href="https://gyg.me/3HlBmZwt"
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          buy the tickets in advance
        </a>{" "}
        and you skip the queue at the gate. On a budget? Strolling the gardens without the ride pass is
        still a lovely evening.
      </>
    ),
  },
  {
    q: "Where to eat smørrebrød in Copenhagen?",
    a: (
      <>
        Smørrebrød — the classic Danish open sandwich — is a lunch thing, best with a beer and a snaps.
        See our top sit-down spots and casual tasting counters in the{" "}
        <Link
          to="/smorrebrod"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          full smørrebrød guide
        </Link>
        .
      </>
    ),
  },
  {
    q: "How do you rent a bike in Copenhagen?",
    a: (
      <>
        Rent from your hotel or hostel, or through a bike app. See our bike
        recommendations and the rules of the bike lanes{" "}
        <Link
          to="/biking"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          here
        </Link>
        .
      </>
    ),
  },
  {
    q: "How do I get around in Copenhagen?",
    a: "Walking, and the metro or trains. Don't take taxis unless necessary — taxis are exceptionally expensive.",
    link: { to: "/transport", label: "The transport guide" },
  },
  {
    q: "Is Copenhagen walkable?",
    a: "Yes — it's HIGHLY recommended to walk around Copenhagen. The centre is compact: most must-sees sit within a 20-minute walk of each other. When crossing the street on foot, watch for bicycle traffic.",
  },
  {
    q: "Can you swim in Copenhagen's harbour?",
    a: (
      <>
        Yes. The harbour water is clean enough for swimming. Tourists usually head for the
        designated harbour baths like Islands Brygge or Sandkaj in Nordhavn — see our{" "}
        <Link
          to="/harbour-swimming"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          harbour swimming guide
        </Link>{" "}
        for every spot.
      </>
    ),
  },
  {
    q: "Is Freetown Christiania safe to visit?",
    a: "Yes. Since 2024 it's much safer to visit, and you're now allowed to take pictures — which wasn't the case before. The street that was once a bit dodgy is now a lively lane with market stalls and small restaurants. Well worth visiting.",
  },
  {
    q: "Is Copenhagen safe?",
    a: "Very — it's one of Europe's safest capitals, and solo travellers regularly rate it as comfortable day and night. The realistic risks are bike-lane collisions and maybe some pickpockets in crowded tourist spots. See each area's safety on the areas page.",
  },
  {
    q: "Is the Copenhagen Card worth it?",
    a: (
      <>
        It depends on how much you pack into one day. We did the maths on a classic sightseeing day
        — Tivoli, Rosenborg Castle and the Glyptoteket, all on the same day. On a weekday, separate
        attraction tickets plus a 24-hour City Pass for inner-city transport come to 580 kr., while
        the Copenhagen Card costs 589 kr. — so 9 kr. cheaper without the card. On a weekend, the same
        tickets (Tivoli is pricier at weekends) plus the City Pass come to 620 kr., making the 589 kr.
        card 31 kr. cheaper. Our conclusion: the Copenhagen Card is only worth it if you visit three
        attractions in one day and use public transport that same day. It pays off fastest with 2–3 of
        the pricier attractions — but fitting them all into a single day can be tough. (We reserve the
        right for calculation errors and price changes.) See our{" "}
        <Link
          to="/transport"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          transport guide
        </Link>{" "}
        for tickets and passes.
      </>
    ),
  },
  {
    q: "Is Copenhagen worth visiting?",
    a: "Absolutely! Copenhagen is compact, walkable, cycle-friendly and clean, with a rare mix of design, food, harbour swimming and easy day trips. Well worth visiting.",
  },
  {
    q: "Copenhagen or Amsterdam?",
    a: "Both, for different reasons. Both are bike-first canal cities, but Copenhagen is calmer, cleaner and more design-led, with swimmable harbour water. Amsterdam is busier, cheaper to reach and has more nightlife and big-name museums. For a relaxed, stylish city break choose Copenhagen; for buzz and lower prices, Amsterdam.",
  },
  {
    q: "What is Hans Christian Andersen's connection to Copenhagen?",
    a: "The fairy-tale writer behind The Little Mermaid, The Ugly Duckling and The Emperor's New Clothes arrived here at 14, lived in three houses on Nyhavn (Nos. 20, 67 and 18), and is buried at Assistens Cemetery — his statues are all over the city.",
    link: { to: "/hc-andersen", label: "Meet him around the city" },
  },
  {
    q: "Should I visit Copenhagen or Stockholm?",
    a: "Both! Copenhagen is smaller and easy to walk around, with a stronger café and cycling culture and a swimmable harbour. Stockholm shines in summer for its archipelago. Food, sights and bikes: Copenhagen. Nature and city life: Stockholm.",
  },
  {
    q: "Should I visit Copenhagen or Oslo?",
    a: "Both! Copenhagen is more charming, with a stronger café and bakery culture and a swimmable harbour. Oslo pairs the city with nature — fjord saunas, island boat trips and forest hikes. Food, bikes and city buzz: Copenhagen. Fjord and outdoors: Oslo.",
  },
  {
    q: "What is a must-do in Copenhagen?",
    a: "Wander Nyhavn, see Amalienborg Castle and wander the old town. Visit Tivoli Gardens and Freetown Christiania. Eat a pastry or three. In summer add a swim at a harbour bath. In winter, swap it for Christmas stall browsing.",
    link: { to: "/copenhagen-itinerary", label: "See our 3-day itinerary" },
  },
  {
    q: "What food is Copenhagen known for?",
    a: "Start with smørrebrød — the open rye sandwiches are the national lunch. From a pølsevogn wagon, grab a hotdog: pick the red boiled sausage or the brown grilled one, then go 'med det hele' (with everything). Don't leave without bakery goods — a kanelsnegl cinnamon swirl is dangerous. In December, add gløgg and æbleskiver at the Christmas markets.",
    links: [
      { to: "/danish-food", label: "The Danish food guide" },
      { to: "/smorrebrod", label: "The smørrebrød guide" },
      { to: "/bakeries", label: "The bakery guide" },
    ],
  },
  {
    q: "What to buy in Copenhagen?",
    a: "Lego from the Lego store on Strøget — the brick was born here. Tins of butter cookies, Anthon Berg chocolate and Toms Skildpadder (chocolate-caramel turtles) for the flight home. Royal Copenhagen porcelain if you're feeling fancy. Salty Danish liquorice, an acquired taste worth acquiring. Local artwork straight from the artists in Freetown Christiania. And Scandi fashion: Ganni, Stine Goya, Samsøe Samsøe and Mads Nørgaard all have flagship stores in the city.",
  },
  {
    q: "What should I not miss in Copenhagen?",
    a: "Wander Nyhavn and the old town. Visit Tivoli Gardens and Freetown Christiania. Eat a pastry or three. In summer add a swim at a harbour bath. In winter, swap it for Christmas stall browsing.",
    link: { to: "/copenhagen-itinerary", label: "See the itinerary" },
  },
  {
    q: "What day trips can you take from Copenhagen?",
    a: "The classics: Bakken's amusement park and deer forest, the Louisiana art museum, Hamlet's castle in Helsingør, Roskilde's Viking ships, the yellow cottages of Dragør — and Malmö in Sweden, 40 minutes over the bridge. All under an hour from the city centre.",
    link: { to: "/trips", label: "See all day trips" },
  },
  {
    q: "How do I get from Copenhagen to Odense?",
    a: "Direct trains from København H run every 30 minutes and take about 1h 50min — book an Orange ticket ahead on DSB for the cheapest fare. Odense is H.C. Andersen's hometown and an easy long day out.",
    link: { to: "/beyond-copenhagen", label: "Our Danish gems guide" },
  },
  {
    q: "Is Tivoli Gardens worth it?",
    a: "Yes — Tivoli is one of the world's oldest amusement parks and as much a garden and concert venue as a ride park. It's especially magical after dark and during the Halloween and Christmas seasons. Go for the atmosphere, gardens and lights as much as the rides.",
  },
  {
    q: "How long should I spend in Tivoli?",
    a: "Depends on whether you just want to walk around or try the rides. About 3 hours is plenty to stroll and soak up the atmosphere — 4–5 hours goes easily if you're doing the rides. It also depends on whether you're planning to eat in there.",
  },
  {
    q: "Is Copenhagen good for kids?",
    a: "Very — Tivoli's rides sit right in the city centre, the Zoo and the hands-on Experimentarium science centre are easy wins, and the harbour baths have shallow kids' pools in summer. Even the Metro is stroller-friendly, with level access and lifts at every station.",
  },
  {
    q: "Do you tip in Copenhagen?",
    a: "No — service is included in the price and staff earn proper wages. Rounding up the bill or leaving ~10% for genuinely great service is appreciated, but never expected. Nobody will chase you down the street for skipping it.",
  },
  {
    q: "When is the best time to visit Copenhagen?",
    a: "Late May to early September is best for long days, harbour swimming and outdoor life, with May and September quieter and better value. December is lovely for Christmas markets and Tivoli lights. Winters are dark and cold but cosy.",
    link: { to: "/copenhagen-in", label: "See the month-by-month guide" },
  },
  {
    q: "Which month is the sunniest in Copenhagen?",
    a: "July — the long, light days peak and the city lives outside. May and June are close seconds.",
    link: { to: "/copenhagen-in", label: "See the month-by-month guide" },
  },
  {
    q: "What can you do in Copenhagen on a rainy day?",
    a: (
      <>
        Plenty — warm up in the Glyptoteket or the National Museum, browse the Black Diamond
        library, or settle in for a long smørrebrød lunch. Our{" "}
        <Link
          to="/rainy-day"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          rainy day guide
        </Link>{" "}
        has a full day of indoor plans, museums included.
      </>
    ),
  },
];

export const Faq = () => (
  <section
    id="faq"
    data-testid="faq-section"
    className="bg-[#F9F8F6] py-12 md:py-28"
  >
    <div className="max-w-[1000px] mx-auto px-5 md:px-10">
      <p className="font-body font-700 text-xs uppercase tracking-[0.3em] eyebrow text-[#DF3226] mb-4">
        Good to know
      </p>
      <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
        Copenhagen, <span className="font-editorial italic font-600">answered</span>
      </h2>
      <p className="font-body text-base text-black/60 mt-5 max-w-xl">
        The questions first-time visitors ask most — how long to stay, when to
        go, what to see and how to do it on a budget.
      </p>

      <div className="mt-10 divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
        {FAQS.map((item, i) => (
          <details
            key={i}
            data-testid={`faq-item-${i}`}
            className="group py-5"
          >
            <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-display font-800 text-lg text-black">
              <span>{item.q}</span>
              <Plus
                size={22}
                className="shrink-0 mt-1 text-[#DF3226] transition-transform duration-300 group-open:rotate-45"
              />
            </summary>
            <p className="font-body text-base text-black/70 mt-3 max-w-3xl leading-relaxed">
              {item.a}
            </p>
            {(item.links || (item.link ? [item.link] : [])).map((l) => (
              <Link
                key={l.to + l.label}
                to={l.to}
                data-testid={`faq-link-${i}-${l.to.replace(/\//g, "")}`}
                className="group flex items-center gap-2 mt-3 font-body font-700 text-base text-black/60 hover:text-[#DF3226] transition-colors w-fit"
              >
                <ArrowRight size={16} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                {l.label}
              </Link>
            ))}
          </details>
        ))}
      </div>
    </div>
  </section>
);
